const LOW_QUALITY_DESCRIPTION_PATTERNS = [
  /\bplay\s+0\s+slot/i,
  /\bfrom\s+0\s+game\s+providers?\b/i,
  /\b0\s+slot\s+games?\b/i,
  /^#\s+/m,
  /<h1[\s>]/i,
];

export function isLowQualityCasinoDescription(text: string): boolean {
  const normalized = String(text ?? '').trim();
  if (!normalized) return true;
  return LOW_QUALITY_DESCRIPTION_PATTERNS.some((pattern) => pattern.test(normalized));
}

export function sanitizeCasinoDescription(
  shortDescription: string,
  bonusShort = '',
): string {
  const description = String(shortDescription ?? '').trim();
  if (!isLowQualityCasinoDescription(description)) return description;
  return String(bonusShort ?? '').trim();
}

export function getCasinoCardDescription(casino: {
  name: string;
  shortDescription: string;
  bonusShort: string;
}): string {
  const description = sanitizeCasinoDescription(casino.shortDescription, casino.bonusShort);
  if (description) return description;
  return '';
}

export function getCasinoLogoAlt(name: string): string {
  const normalized = String(name ?? '').trim();
  return normalized ? `${normalized} logo` : 'Casino logo';
}

export function extractReferenceNames(value: unknown, limit = 6): string[] {
  const arr = Array.isArray(value) ? value : (value as { data?: unknown[] })?.data;
  if (!Array.isArray(arr)) return [];
  return arr
    .map((item) => String((item as { name?: unknown })?.name ?? '').trim())
    .filter(Boolean)
    .slice(0, limit);
}

export type CasinoCatalogSort = 'featured' | 'rating' | 'name' | 'new';

export type CasinoQuickFilterId = 'mga' | 'crypto' | 'lowDeposit';

export const CASINO_QUICK_FILTER_IDS: CasinoQuickFilterId[] = ['mga', 'crypto', 'lowDeposit'];

export function sortCasinoCatalogItems<
  T extends { name: string; computedRating: number | string; ratingCount?: number; cmsId?: number },
>(items: T[], sort: CasinoCatalogSort): T[] {
  if (sort === 'featured') return items;
  const copy = [...items];
  if (sort === 'name') {
    copy.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return copy;
  }
  if (sort === 'new') {
    copy.sort((a, b) => (b.cmsId ?? 0) - (a.cmsId ?? 0));
    return copy;
  }
  copy.sort((a, b) => {
    const ratingDiff = Number(b.computedRating || 0) - Number(a.computedRating || 0);
    if (ratingDiff !== 0) return ratingDiff;
    return (b.ratingCount ?? 0) - (a.ratingCount ?? 0);
  });
  return copy;
}

export function countActiveCasinoFilters(filters: {
  country?: string;
  license?: string;
  minDeposit?: string;
  minRating?: string;
  paymentMethods?: string[];
}): number {
  let count = 0;
  if (filters.country) count += 1;
  if (filters.license) count += 1;
  if (filters.minDeposit) count += 1;
  if (filters.minRating) count += 1;
  count += filters.paymentMethods?.length ?? 0;
  return count;
}

export function isCasinoQuickFilterActive(
  id: CasinoQuickFilterId,
  filters: {
    license?: string;
    minDeposit?: string;
    paymentMethods?: string[];
  },
): boolean {
  if (id === 'mga') return filters.license === 'MGA';
  if (id === 'crypto') return filters.paymentMethods?.includes('Bitcoin') ?? false;
  if (id === 'lowDeposit') return filters.minDeposit === '10';
  return false;
}
